<template>
  <div class="content-wrapper">
    <!-- Content Header (Page header) -->
    <section class="content-header">
      <h1>
        Génération de facture
        <small></small>
      </h1>
      <ol class="breadcrumb">
        <li>
          <router-link :to="{ name: 'Dashboard'}">
            <i class="fa fa-dashboard"></i>
            Accueil
          </router-link>
        </li>
        <li><a href="#">Outils</a></li>
        <li class="active">Génération facture</li>
      </ol>
    </section>

    <section class="content">
      <div class="row">
        <div class="col-xs-12">
          <div class="box box-primary">
            <div class="box-body barre-actions">
              <button type="button" class="btn btn-primary"
                      :disabled="generationPDF"
                      @click="telechargerPdf()">
                <i class="fa fa-download"></i> Télécharger le PDF
              </button>
              <button type="button" class="btn btn-default" @click="enregistrer()">
                <i class="fa fa-save"></i> Enregistrer
                <small class="text-muted">Ctrl+S</small>
              </button>
              <span class="separateur-actions"></span>
              <button type="button" class="btn btn-default"
                      title="Annuler (Ctrl+Z)"
                      :disabled="!peutAnnuler"
                      @click="annuler()">
                <i class="fa fa-undo"></i>
              </button>
              <button type="button" class="btn btn-default"
                      title="Refaire (Ctrl+Maj+Z)"
                      :disabled="!peutRefaire"
                      @click="refaire()">
                <i class="fa fa-repeat"></i>
              </button>
              <span class="separateur-actions"></span>
              <button type="button" class="btn btn-default" @click="nouvelleFacture()">
                <i class="fa fa-file-o"></i> Nouvelle facture
              </button>

              <span class="pull-right indicateur-sauvegarde">
                <small v-if="derniereSauvegarde" class="text-muted">
                  <i class="fa fa-check"></i> Enregistré à {{ derniereSauvegarde }}
                </small>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="row" v-if="stockageIndisponible">
        <div class="col-xs-12">
          <div class="alert alert-warning">
            <i class="fa fa-warning"></i>
            Sauvegarde automatique indisponible dans ce navigateur — téléchargez votre PDF
            avant de quitter la page.
          </div>
        </div>
      </div>

      <div class="row" v-if="brouillonRecupere">
        <div class="col-xs-12">
          <div class="alert alert-info">
            <i class="fa fa-history"></i>
            Brouillon récupéré automatiquement.
            <a href="#" @click.prevent="nouvelleFacture()">Repartir d'une facture vierge</a>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12 col-sm-12 col-md-7 col-lg-7">
          <facture-editeur :facture="facture"
                           :totaux="totaux"
                           :suggestions="memos"
                           :carnet="carnet"
                           :options-indisponibles="optionsIndisponibles"
                           :numero-en-doublon="numeroEnDoublon"
                           :qr-possible="!!qrDataUrl"
                           @modifier="appliquerModification"
                           @numeroter="renumeroter()"
                           @appliquer-modele="appliquerModeleFacture"
                           @echeance-jours="definirEcheance"
                           @activer-option="activerOption"
                           @desactiver-option="desactiverOption"
                           @logo-choisi="choisirLogo"
                           @logo-federation="reprendreLogoFederation()"
                           @generer-communication="genererCommunication()"
                           @client-choisi="remplacerClient"
                           @modifier-client="modifierClient"
                           @memoriser-client="memoriserClient()"
                           @oublier-client="oublierClient"
                           @ajouter-ligne="ajouterLigne()"
                           @supprimer-ligne="supprimerLigne"
                           @dupliquer-ligne="dupliquerLigne"
                           @deplacer-ligne="deplacerLigne"
                           @modifier-ligne="modifierLigne">
          </facture-editeur>
        </div>

        <div class="col-xs-12 col-sm-12 col-md-5 col-lg-5">
          <div class="box box-primary colonne-apercu">
            <div class="overlay" v-if="generationPDF">
              <i class="fa fa-refresh fa-spin"></i>
            </div>
            <div class="box-header with-border">
              <h3 class="box-title">Aperçu</h3>
            </div>
            <div class="box-body">
              <facture-apercu :facture="facture"
                              :totaux="totaux"
                              :qr-data-url="qrDataUrl"
                              :logo-data-url="logoAffiche">
              </facture-apercu>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12">
          <facture-historique :factures="historique"
                              @ouvrir="ouvrirFacture"
                              @dupliquer="dupliquerFacture"
                              @supprimer="supprimerFacture"
                              @importer="importerFactures">
          </facture-historique>
        </div>
      </div>
    </section>
  </div>
</template>

<script type="text/babel">
  import {mapGetters} from 'vuex'
  import FactureEditeur from './Facture/FactureEditeur'
  import FactureApercu from './Facture/FactureApercu'
  import FactureHistorique from './Facture/FactureHistorique'
  import {factureVierge, ligneVierge, normaliser, dateFrValide, dateFrPlusJours} from '../../utils/facture/modele'
  import {calculerTotaux, normaliserMontant} from '../../utils/facture/calculs'
  import {prochainNumero, communicationStructuree, numeroDejaUtilise} from '../../utils/facture/numerotation'
  import {chargerFormatNumerotation} from '../../utils/facture/depot'
  import {creerHistorique} from '../../utils/facture/historiqueEdition'
  import {qrDisponible, genererQrDataUrl} from '../../utils/facture/epc'
  import {IBAN_FEDERATION, chargerLogoFederation} from '../../utils/facture/federation'
  import {aplatirSurBlanc} from '../../utils/facture/logo'
  import {telechargerFacturePdf} from '../../utils/facture/pdf'
  import {appliquerModele} from '../../utils/facture/modeles'
  import * as stockage from '../../utils/facture/stockage'

  const DELAI_AUTOSAUVEGARDE = 400
  const TAILLE_MAX_LOGO = 1024 * 1024

  export default {
    name: 'GenerationFacture',
    components: {FactureEditeur, FactureApercu, FactureHistorique},
    data () {
      return {
        facture: factureVierge(),
        historique: [],
        memos: [],
        carnet: [],
        compteurs: {},
        qrDataUrl: null,
        logoFederationDataUrl: null,
        generationPDF: false,
        derniereSauvegarde: null,
        stockageIndisponible: false,
        brouillonRecupere: false,
        peutAnnuler: false,
        peutRefaire: false,
        minuteur: null,
        // Évite d'empiler un instantané lorsque c'est l'annulation elle-même
        // qui remplace la facture
        applicationHistorique: false
      }
    },
    computed: {
      ...mapGetters({
        getUserSelected: 'getUserSelected',
        getClubSelected: 'getClubSelected'
      }),
      totaux () {
        return calculerTotaux(this.facture)
      },
      optionsIndisponibles () {
        return qrDisponible() ? [] : ['qr']
      },
      numeroEnDoublon () {
        return numeroDejaUtilise(this.facture.numero, this.historique, this.facture.id)
      },
      /**
       * Le logo de la fédération est résolu à l'affichage et jamais stocké ;
       * un logo téléversé par l'utilisateur, lui, vit dans le modèle.
       */
      logoAffiche () {
        return this.facture.emetteur.logoFederation
          ? this.logoFederationDataUrl
          : this.facture.emetteur.logoDataUrl
      }
    },
    watch: {
      facture: {
        deep: true,
        handler () {
          this.planifierSauvegarde()
        }
      }
    },
    created () {
      this.pileEdition = creerHistorique()
      this.stockageIndisponible = !stockage.stockageDisponible()
      this.historique = stockage.lireHistorique()
      this.memos = stockage.lireMemos()
      this.carnet = stockage.lireCarnet()
      this.compteurs = stockage.lireCompteurs()

      const brouillon = stockage.lireBrouillon()
      if (brouillon) {
        this.facture = brouillon
        this.brouillonRecupere = true
      } else {
        this.facture = this.construireFacture()
        this.prerremplirDepuisSelection()
      }

      this.pileEdition.empiler(this.facture)
      // L'affectation de `facture` a déjà réveillé le watcher : sans ce drapeau,
      // le premier passage empilerait un doublon de l'état initial et « annuler »
      // semblerait sans effet
      this.applicationHistorique = true
      this.rafraichirQr()
      this.chargerLogo()
    },
    mounted () {
      document.addEventListener('keydown', this.surRaccourci)
      // Le préfixe des numéros vient des paramètres de facturation : un échec
      // laisse simplement le format historique en place.
      chargerFormatNumerotation({forcer: true})
    },
    beforeDestroy () {
      document.removeEventListener('keydown', this.surRaccourci)
      if (this.minuteur) clearTimeout(this.minuteur)
    },
    methods: {
      chargerLogo () {
        if (this.logoFederationDataUrl) return
        const composant = this
        chargerLogoFederation().then(function (dataUrl) {
          composant.logoFederationDataUrl = dataUrl
        })
      },

      /* ---------- Cycle de vie de la facture ---------- */

      construireFacture () {
        const facture = factureVierge()
        facture.numero = this.consommerNumero()
        // Le bloc paiement est présent d'office : sa communication doit l'être aussi
        facture.paiement.communicationStructuree = communicationStructuree(facture.numero)
        return facture
      },

      /**
       * Le compteur est consommé dès l'attribution : deux factures ouvertes côte
       * à côte ne doivent jamais porter le même numéro, quitte à laisser un trou
       * si l'une est abandonnée.
       */
      consommerNumero () {
        const resultat = prochainNumero(this.compteurs, new Date().getFullYear())
        this.compteurs = resultat.compteurs
        stockage.ecrireCompteurs(resultat.compteurs)
        return resultat.numero
      },

      // Un membre ou un club déjà sélectionné ailleurs dans l'application est
      // repris comme destinataire par défaut
      prerremplirDepuisSelection () {
        const membre = this.getUserSelected
        if (membre && membre.fullName) {
          this.facture.client = Object.assign({}, this.facture.client, {
            source: 'membre',
            idSource: membre.id,
            nom: membre.fullName,
            adresse: membre.adresse || '',
            cp: membre.cp || '',
            ville: membre.ville || ''
          })
          return
        }
        const club = this.getClubSelected
        if (club && club.club) {
          this.facture.client = Object.assign({}, this.facture.client, {
            source: 'club',
            idSource: club.id,
            nom: club.club,
            adresse: club.adresse || '',
            cp: club.cp || '',
            ville: club.ville || ''
          })
        }
      },

      nouvelleFacture () {
        if (!confirm('Repartir d\'une facture vierge ? La facture en cours sera perdue si elle n\'est pas enregistrée.')) return
        this.facture = this.construireFacture()
        this.brouillonRecupere = false
        this.pileEdition.vider()
        this.pileEdition.empiler(this.facture)
        this.majEtatHistorique()
        this.qrDataUrl = null
      },

      enregistrer () {
        const facture = normaliser(this.facture)
        this.facture = facture
        this.historique = stockage.enregistrerDansHistorique(facture)
        this.memos = stockage.memoriserLignes(facture.lignes)
        this.brouillonRecupere = false
        this.$noty.success('Facture ' + (facture.numero || '') + ' enregistrée')
      },

      /* ---------- Sauvegarde automatique et pile d'annulation ---------- */

      planifierSauvegarde () {
        if (this.minuteur) clearTimeout(this.minuteur)
        const composant = this
        this.minuteur = setTimeout(function () {
          composant.sauverBrouillon()
          if (composant.applicationHistorique) {
            composant.applicationHistorique = false
          } else {
            composant.pileEdition.empiler(composant.facture)
          }
          composant.majEtatHistorique()
          composant.rafraichirQr()
        }, DELAI_AUTOSAUVEGARDE)
      },

      sauverBrouillon () {
        if (this.stockageIndisponible) return
        if (stockage.ecrireBrouillon(this.facture)) {
          const maintenant = new Date()
          this.derniereSauvegarde = ('0' + maintenant.getHours()).slice(-2) + ':' +
            ('0' + maintenant.getMinutes()).slice(-2)
        } else {
          // Quota dépassé en cours de route : on prévient une seule fois
          this.stockageIndisponible = true
        }
      },

      majEtatHistorique () {
        this.peutAnnuler = this.pileEdition.peutAnnuler()
        this.peutRefaire = this.pileEdition.peutRefaire()
      },

      annuler () {
        const etat = this.pileEdition.annuler()
        if (!etat) return
        this.applicationHistorique = true
        this.facture = etat
        this.majEtatHistorique()
      },

      refaire () {
        const etat = this.pileEdition.refaire()
        if (!etat) return
        this.applicationHistorique = true
        this.facture = etat
        this.majEtatHistorique()
      },

      surRaccourci (evenement) {
        if (!evenement.ctrlKey && !evenement.metaKey) return
        const touche = evenement.key ? evenement.key.toLowerCase() : ''

        if (touche === 's') {
          evenement.preventDefault()
          this.enregistrer()
          return
        }
        if (touche === 'z') {
          evenement.preventDefault()
          if (evenement.shiftKey) this.refaire()
          else this.annuler()
          return
        }
        if (touche === 'y') {
          evenement.preventDefault()
          this.refaire()
        }
      },

      /* ---------- Modifications du modèle ---------- */

      appliquerModification (evenement) {
        const morceaux = evenement.chemin.split('.')
        let cible = this.facture
        for (let index = 0; index < morceaux.length - 1; index += 1) {
          cible = cible[morceaux[index]]
        }
        cible[morceaux[morceaux.length - 1]] = evenement.valeur
      },

      definirEcheance (jours) {
        const calculee = dateFrPlusJours(this.facture.dateEmission, jours)
        if (!calculee) {
          this.$noty.error('Indiquez d\'abord une date d\'émission valide (jj/mm/aaaa)')
          return
        }
        this.facture.dateEcheance = calculee
      },

      renumeroter () {
        const ancienne = communicationStructuree(this.facture.numero)
        const suivaitLeNumero = !this.facture.paiement.communicationStructuree ||
          this.facture.paiement.communicationStructuree === ancienne

        this.facture.numero = this.consommerNumero()

        // On ne réécrit pas une communication que l'utilisateur a saisie lui-même
        if (suivaitLeNumero) {
          this.facture.paiement.communicationStructuree =
            communicationStructuree(this.facture.numero)
        }
      },

      genererCommunication () {
        if (!this.facture.numero) {
          this.$noty.error('Indiquez d\'abord un numéro de facture')
          return
        }
        this.facture.paiement.communicationStructuree = communicationStructuree(this.facture.numero)
      },

      /* ---------- Modèles et génération en série ---------- */

      appliquerModeleFacture (cle) {
        if (!cle) {
          this.facture.modele = null
          return
        }
        if (this.lignesRemplies() &&
          !confirm('Appliquer ce modèle remplacera les lignes saisies. Continuer ?')) {
          return
        }
        this.facture = appliquerModele(this.facture, cle)
      },

      lignesRemplies () {
        return this.facture.lignes.some(function (ligne) {
          return String(ligne.description || '').trim() || normaliserMontant(ligne.prixUnitaire)
        })
      },

      /* ---------- Champs optionnels ---------- */

      activerOption (cle) {
        this.facture.options[cle] = true

        if (cle === 'echeance' && !this.facture.dateEcheance) {
          this.definirEcheance(30)
        }
        if (cle === 'qr') {
          // Un QR SEPA n'a de sens qu'avec des coordonnées de paiement
          this.facture.options.paiement = true
        }
        if ((cle === 'paiement' || cle === 'qr') && !this.facture.paiement.beneficiaire) {
          this.facture.paiement.beneficiaire = this.facture.emetteur.nom || ''
        }
        if ((cle === 'paiement' || cle === 'qr') && !this.facture.paiement.iban) {
          this.facture.paiement.iban = IBAN_FEDERATION
        }
      },

      desactiverOption (cle) {
        if (this.optionRemplie(cle) &&
          !confirm('Retirer ce bloc effacera les valeurs qu\'il contient. Continuer ?')) {
          return
        }
        this.facture.options[cle] = false
        this.reinitialiserOption(cle)
      },

      optionRemplie (cle) {
        const facture = this.facture
        switch (cle) {
          case 'tva':
            return !!facture.tva.taux || !!facture.tva.mentionExoneration
          case 'remise':
            return !!facture.remise.valeur
          case 'frais':
            return !!facture.frais
          case 'notes':
            return !!facture.notes
          case 'conditions':
            return !!facture.conditionsPaiement
          case 'reference':
            return !!facture.reference
          case 'echeance':
            return !!facture.dateEcheance
          case 'logo':
            // Le logo de la fédération se remet en un clic : seul un logo
            // téléversé mérite une confirmation avant d'être perdu
            return !!facture.emetteur.logoDataUrl
          case 'paiement':
            return !!(facture.paiement.iban || facture.paiement.communicationStructuree)
          default:
            return false
        }
      },

      reinitialiserOption (cle) {
        const facture = this.facture
        if (cle === 'tva') {
          facture.tva.taux = 0
          facture.tva.mentionExoneration = ''
        } else if (cle === 'remise') {
          facture.remise.valeur = 0
        } else if (cle === 'frais') {
          facture.frais = 0
        } else if (cle === 'notes') {
          facture.notes = ''
        } else if (cle === 'conditions') {
          facture.conditionsPaiement = ''
        } else if (cle === 'reference') {
          facture.reference = ''
        } else if (cle === 'echeance') {
          facture.dateEcheance = null
        } else if (cle === 'logo') {
          facture.emetteur.logoDataUrl = null
          facture.emetteur.logoFederation = true
        } else if (cle === 'paiement') {
          facture.paiement.iban = ''
          facture.paiement.bic = ''
          facture.paiement.communicationStructuree = ''
          facture.options.qr = false
          this.qrDataUrl = null
        } else if (cle === 'qr') {
          this.qrDataUrl = null
        }
      },

      choisirLogo (evenement) {
        const fichier = evenement.target.files && evenement.target.files[0]
        if (!fichier) return

        if (['image/png', 'image/jpeg'].indexOf(fichier.type) === -1) {
          this.$noty.error('Format de logo non accepté : PNG ou JPG uniquement')
          evenement.target.value = ''
          return
        }
        if (fichier.size > TAILLE_MAX_LOGO) {
          this.$noty.error('Logo trop lourd : 1 Mo maximum')
          evenement.target.value = ''
          return
        }

        const composant = this
        const lecteur = new FileReader()
        lecteur.onload = function () {
          aplatirSurBlanc(lecteur.result).then(function (prepare) {
            composant.facture.emetteur.logoDataUrl = prepare
            composant.facture.emetteur.logoFederation = false
          })
        }
        lecteur.onerror = function () {
          composant.$noty.error('Lecture du logo impossible')
        }
        lecteur.readAsDataURL(fichier)
      },

      /* ---------- Client ---------- */

      reprendreLogoFederation () {
        this.facture.emetteur.logoDataUrl = null
        this.facture.emetteur.logoFederation = true
        this.chargerLogo()
      },

      remplacerClient (client) {
        this.facture.client = Object.assign({}, this.facture.client, client)
      },

      modifierClient (evenement) {
        this.facture.client[evenement.champ] = evenement.valeur
      },

      memoriserClient () {
        this.carnet = stockage.ajouterAuCarnet(this.facture.client)
        this.$noty.success('Client ajouté au carnet')
      },

      oublierClient (index) {
        this.carnet = stockage.retirerDuCarnet(index)
      },

      /* ---------- Lignes ---------- */

      ajouterLigne () {
        this.facture.lignes.push(ligneVierge())
      },

      supprimerLigne (index) {
        if (this.facture.lignes.length === 1) {
          // On ne descend jamais à zéro ligne : on remet la dernière à blanc
          this.facture.lignes.splice(0, 1, ligneVierge())
          return
        }
        this.facture.lignes.splice(index, 1)
      },

      dupliquerLigne (index) {
        const copie = Object.assign({}, this.facture.lignes[index], {id: ligneVierge().id})
        this.facture.lignes.splice(index + 1, 0, copie)
      },

      deplacerLigne (evenement) {
        const cible = evenement.index + evenement.delta
        if (cible < 0 || cible >= this.facture.lignes.length) return
        const lignes = this.facture.lignes.slice()
        const deplacee = lignes.splice(evenement.index, 1)[0]
        lignes.splice(cible, 0, deplacee)
        this.facture.lignes = lignes
      },

      modifierLigne (evenement) {
        this.facture.lignes[evenement.index][evenement.champ] = evenement.valeur
      },

      /* ---------- Historique des factures ---------- */

      ouvrirFacture (facture) {
        this.facture = normaliser(facture)
        this.brouillonRecupere = false
        this.pileEdition.vider()
        this.pileEdition.empiler(this.facture)
        this.majEtatHistorique()
      },

      dupliquerFacture (facture) {
        const copie = normaliser(facture)
        copie.id = factureVierge().id
        copie.numero = this.consommerNumero()
        copie.dateEmission = factureVierge().dateEmission
        this.facture = copie
        this.pileEdition.vider()
        this.pileEdition.empiler(this.facture)
        this.majEtatHistorique()
        this.$noty.success('Facture dupliquée sous le numéro ' + copie.numero)
      },

      supprimerFacture (facture) {
        if (!confirm('Supprimer définitivement la facture ' + (facture.numero || '') + ' ?')) return
        this.historique = stockage.supprimerDeHistorique(facture.id)
      },

      importerFactures (liste) {
        const connues = {}
        const fusionnees = []
        liste.concat(this.historique).forEach(function (entree) {
          const facture = normaliser(entree)
          if (connues[facture.id]) return
          connues[facture.id] = true
          fusionnees.push(facture)
        })
        stockage.ecrireHistorique(fusionnees)
        this.historique = stockage.lireHistorique()
        this.$noty.success(this.historique.length + ' facture(s) dans l\'historique')
      },

      /* ---------- QR et PDF ---------- */

      rafraichirQr () {
        if (!this.facture.options.qr) {
          this.qrDataUrl = null
          return
        }
        const composant = this
        genererQrDataUrl({
          beneficiaire: this.facture.paiement.beneficiaire,
          iban: this.facture.paiement.iban,
          bic: this.facture.paiement.bic,
          montant: this.totaux.total,
          communication: this.facture.paiement.communicationStructuree
        }).then(function (dataUrl) {
          composant.qrDataUrl = dataUrl
        })
      },

      telechargerPdf () {
        if (!dateFrValide(this.facture.dateEmission)) {
          this.$noty.error('Indiquez une date valide au format jj/mm/aaaa')
          return
        }
        if (this.facture.dateEcheance && !dateFrValide(this.facture.dateEcheance)) {
          this.$noty.error('La date d\'échéance doit être au format jj/mm/aaaa')
          return
        }

        const composant = this
        this.generationPDF = true
        telechargerFacturePdf(this.facture, this.totaux, this.qrDataUrl, this.logoAffiche).then(function () {
          composant.generationPDF = false
        }, function (erreur) {
          console.error('Erreur lors de la génération du PDF de la facture:', erreur)
          composant.generationPDF = false
          composant.$noty.error('La génération du PDF a échoué')
        })
      }
    }
  }
</script>

<style scoped>
  .barre-actions .btn {
    margin-right: 6px;
  }

  .separateur-actions {
    display: inline-block;
    width: 1px;
    height: 22px;
    margin: 0 8px -5px 2px;
    background: #e4e4e4;
  }

  .indicateur-sauvegarde {
    padding-top: 8px;
  }

  .colonne-apercu .box-body {
    padding: 8px;
    background: #f4f4f4;
  }
</style>



// WEBPACK FOOTER //
// src/components/Outils/GenerationFacture.vue